/**
 * The last hour of Stages and queues (`GET /admin/flow/history`, paige-v1.yaml FlowHistory): a minute paige-watch
 * recorded, drawn on the same picture. Only what was recorded is drawn: counts by place and colour, the releases and
 * alerts of the window; never movement, which a minute does not keep. Pure.
 */
import type { FlowNote, FlowStatus, FlowTone } from './flow-state';
import type { FlowCell, FlowPlace } from './flow-types';

export interface FlowMinutePlace {
  place: string;
  waiting: number;
  working: number;
  /** Held by design, or parked and the customer's. */
  held: number;
  /** Tried again, or parked and put back by Paige itself. */
  retrying: number;
  /** Parked and ours: a person at Ademero must act. */
  stuck: number;
}

export interface FlowMinute {
  at: string;
  places: FlowMinutePlace[];
}

export interface FlowHistoryRelease {
  at: string;
  build: string | null;
  parts: string[];
}

export interface FlowHistoryAlert {
  key: string;
  severity: 'info' | 'warn' | 'critical';
  title: string;
  openedAt: string;
  closedAt: string | null;
}

export interface FlowHistory {
  asOf: string;
  testsHidden: boolean;
  keptHours: number;
  minutes: FlowMinute[];
  releases: FlowHistoryRelease[];
  alerts: FlowHistoryAlert[];
}

const EMPTY: FlowMinutePlace = {
  place: '',
  waiting: 0,
  working: 0,
  held: 0,
  retrying: 0,
  stuck: 0,
};

/** A place in a minute; nothing there is all zeros. */
export function placeAt(minute: FlowMinute, place: FlowPlace): FlowMinutePlace {
  return minute.places.find((p) => p.place === place) ?? { ...EMPTY, place };
}

/** Everything at a place in a minute. */
export const hereOf = (p: FlowMinutePlace): number =>
  p.waiting + p.working + p.held + p.retrying + p.stuck;

/** A minute's totals across the picture, for the scrubber's bars. */
export function totalsOf(minute: FlowMinute): Omit<FlowMinutePlace, 'place'> {
  return minute.places.reduce(
    (t, p) => ({
      waiting: t.waiting + p.waiting,
      working: t.working + p.working,
      held: t.held + p.held,
      retrying: t.retrying + p.retrying,
      stuck: t.stuck + p.stuck,
    }),
    { waiting: 0, working: 0, held: 0, retrying: 0, stuck: 0 },
  );
}

const pastNote = (kind: FlowNote['kind'], tone: FlowNote['tone'], count: number): FlowNote => ({
  kind,
  tone,
  count,
  reason: null,
  seconds: 0,
  cause: null,
});

/** A place's colour in a recorded minute, by the same rule as now: red only for work of ours that parked. */
export function pastStatus(p: FlowMinutePlace): FlowStatus {
  const notes = [
    ...(p.stuck > 0 ? [pastNote('ours', 'stuck', p.stuck)] : []),
    ...(p.retrying > 0 ? [pastNote('retry', 'slow', p.retrying)] : []),
    ...(p.held > 0 ? [pastNote('hold', 'held', p.held)] : []),
  ];
  const here = hereOf(p);
  const tone: FlowTone =
    p.stuck > 0
      ? 'stuck'
      : p.retrying > 0
        ? 'slow'
        : p.working + p.waiting > 0
          ? 'moving'
          : p.held > 0
            ? 'held'
            : 'idle';
  return { tone, notes, here, queuedMinutes: null };
}

/** A cell as a recorded minute knew it: its counts, and no movement (a minute keeps none). */
export function pastCell(cell: FlowCell, p: FlowMinutePlace): FlowCell {
  return {
    ...cell,
    waiting: p.waiting,
    working: p.working,
    holds: [],
    retries: [],
    parked: [],
    slow: 0,
    pagesPerMinute: 0,
    pages15m: 0,
    ticks: cell.ticks.map(() => 0),
    series: cell.series.map(() => 0),
  };
}

/** Where on the hour a time falls, 0 to 1, from the first recorded minute to now. */
export function positionOf(history: FlowHistory, at: string): number {
  const start = Date.parse(history.minutes[0]?.at ?? history.asOf);
  const end = Date.parse(history.asOf);
  if (!(end > start)) return 1;
  return Math.min(1, Math.max(0, (Date.parse(at) - start) / (end - start)));
}

/** The minute that starts closest before a time, or null when none was recorded by then. */
export function minuteIndexAt(history: FlowHistory, at: number): number | null {
  let found: number | null = null;
  history.minutes.forEach((m, i) => {
    if (Date.parse(m.at) <= at) found = i;
  });
  return found;
}

/** A minute in a sentence, for the slider's spoken value: "14:05: 3 stuck, 12 in flight". */
export function minuteWords(minute: FlowMinute, time: (iso: string) => string): string {
  const t = totalsOf(minute);
  const parts = [
    t.stuck > 0 ? `${t.stuck} need us` : null,
    t.retrying > 0 ? `${t.retrying} retrying` : null,
    `${t.waiting + t.working} in flight`,
    t.held > 0 ? `${t.held} held` : null,
  ].filter(Boolean);
  return `${time(minute.at)}: ${parts.join(', ')}`;
}
